// src/components/ProcessingResults/DraggableCell.jsx
import React, { useRef } from 'react';
import { Group, Rect, Circle } from 'react-konva';

const DraggableCell = ({
  cell,
  isSelected,
  onDragEnd,
  onResize,
  onClick,
  onHover,
  stageSize
}) => {
  const groupRef = useRef();
  const isDragging = useRef(false);
  const isResizing = useRef(false);
  const dragStartPos = useRef({ x: 0, y: 0 });

  const handleDragStart = (e) => {
    isDragging.current = true;
    const group = e.target;
    dragStartPos.current = { x: group.x(), y: group.y() };
  };

  const handleDragMove = (e) => {
    // Only handle group dragging, not resize handle dragging
    if (!isDragging.current || isResizing.current) return;

    const group = e.target;

    // Make sure we're dealing with the group, not a child element
    if (group.getClassName() !== 'Group') return;

    const rect = group.findOne('Rect');
    if (!rect) return;

    // Get current position and size
    const x = group.x();
    const y = group.y();
    const width = rect.width();
    const height = rect.height();

    // Apply boundaries to keep cell within stage
    const boundedX = Math.max(0, Math.min(x, stageSize.width - width));
    const boundedY = Math.max(0, Math.min(y, stageSize.height - height));

    // Update position if it was out of bounds
    if (x !== boundedX || y !== boundedY) {
      group.x(boundedX);
      group.y(boundedY);
    }
  };

  const handleDragEnd = (e) => {
    if (!isDragging.current || isResizing.current) return;

    const group = e.target;

    // Make sure we're dealing with the group
    if (group.getClassName() !== 'Group') return;

    const currentPos = { x: group.x(), y: group.y() };
    const hasMoved = Math.abs(currentPos.x - dragStartPos.current.x) > 2 ||
                     Math.abs(currentPos.y - dragStartPos.current.y) > 2;

    isDragging.current = false;

    if (hasMoved) {
      onDragEnd({
        x: group.x(),
        y: group.y()
      });
    }
  };

  const handleResizeStart = () => {
    isResizing.current = true;
  };

  const handleResizeEnd = (e) => {
    if (!isResizing.current) return;
    isResizing.current = false;

    const group = groupRef.current;
    if (!group) return;

    const rect = group.findOne('Rect');
    if (!rect) return;

    const newWidth = rect.width() * rect.scaleX();
    const newHeight = rect.height() * rect.scaleY();

    // Reset scale to 1 and update actual dimensions
    rect.scaleX(1);
    rect.scaleY(1);
    rect.width(newWidth);
    rect.height(newHeight);

    // Apply boundaries after resize
    const x = group.x();
    const y = group.y();
    const boundedX = Math.max(0, Math.min(x, stageSize.width - newWidth));
    const boundedY = Math.max(0, Math.min(y, stageSize.height - newHeight));

    if (x !== boundedX || y !== boundedY) {
      group.x(boundedX);
      group.y(boundedY);
    }

    onResize(
      { width: newWidth, height: newHeight },
      { x: group.x(), y: group.y() }
    );
  };

  const handleClick = (e) => {
    // Only handle click if not dragging or resizing and if it's a simple click
    if (!isDragging.current && !isResizing.current) {
      e.cancelBubble = true;
      onClick();
    }
  };

  const handleRectClick = (e) => {
    // Handle clicks specifically on the rectangle
    if (!isDragging.current && !isResizing.current) {
      e.cancelBubble = true;
      onClick();
    }
  };

  const handleMouseEnter = () => {
    const confidence = cell.confidence ? (cell.confidence * 100).toFixed(1) : 'N/A';
    const status = cell.isComputed ? 'Computed' : 'Detected';
    onHover(`Cell (${cell.row}, ${cell.col}) - ${status} - Confidence: ${confidence}%`);
  };

  const handleMouseLeave = () => {
    onHover('');
  };

  // Handle size for resize handles
  const handleSize = 8;
  const minSize = 10;

  // Ensure cell dimensions are valid
  const cellWidth = Math.max(cell.width, minSize);
  const cellHeight = Math.max(cell.height, minSize);

  return (
    <Group
      ref={groupRef}
      x={cell.x}
      y={cell.y}
      draggable={isSelected}
      onDragStart={handleDragStart}
      onDragMove={handleDragMove}
      onDragEnd={handleDragEnd}
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      dragBoundFunc={(pos) => {
        // Ensure dragging stays within bounds
        return {
          x: Math.max(0, Math.min(pos.x, stageSize.width - cellWidth)),
          y: Math.max(0, Math.min(pos.y, stageSize.height - cellHeight))
        };
      }}
    >
      {/* Main rectangle */}
      <Rect
        width={cellWidth}
        height={cellHeight}
        stroke={isSelected ? '#0066cc' : '#ff6600'}
        strokeWidth={isSelected ? 2 : 1}
        fill="transparent"
        dash={cell.isComputed ? [5, 5] : []}
        onClick={handleRectClick}
        listening={true} // Allow rect to receive events for selection
      />

      {/* Resize handles - only show when selected */}
      {isSelected && (
        <>
          {/* Top-left handle */}
          <Circle
            x={0}
            y={0}
            radius={handleSize / 2}
            fill="#0066cc"
            stroke="white"
            strokeWidth={1}
            draggable
            onDragStart={handleResizeStart}
            onClick={(e) => e.cancelBubble = true} // Prevent handle clicks from bubbling
            onDragMove={(e) => {
              if (!isResizing.current) return;

              const group = groupRef.current;
              if (!group) return;

              const rect = group.findOne('Rect');
              if (!rect) return;

              const handle = e.target;
              const newX = handle.x();
              const newY = handle.y();

              // Update rectangle size and position
              const newWidth = rect.width() - newX;
              const newHeight = rect.height() - newY;

              if (newWidth > minSize && newHeight > minSize) {
                // Check boundaries
                const groupX = group.x();
                const groupY = group.y();
                const finalX = groupX + newX;
                const finalY = groupY + newY;

                if (finalX >= 0 && finalY >= 0 &&
                    finalX + newWidth <= stageSize.width &&
                    finalY + newHeight <= stageSize.height) {
                  rect.width(newWidth);
                  rect.height(newHeight);
                  group.x(finalX);
                  group.y(finalY);
                  handle.x(0);
                  handle.y(0);
                }
              }
            }}
            onDragEnd={handleResizeEnd}
          />

          {/* Top-right handle */}
          <Circle
            x={cellWidth}
            y={0}
            radius={handleSize / 2}
            fill="#0066cc"
            stroke="white"
            strokeWidth={1}
            draggable
            onDragStart={handleResizeStart}
            onClick={(e) => e.cancelBubble = true}
            onDragMove={(e) => {
              if (!isResizing.current) return;

              const group = groupRef.current;
              if (!group) return;

              const rect = group.findOne('Rect');
              if (!rect) return;

              const handle = e.target;
              const newWidth = handle.x();
              const newY = handle.y();

              if (newWidth > minSize && rect.height() - newY > minSize) {
                // Check boundaries
                const groupX = group.x();
                const groupY = group.y();
                const finalY = groupY + newY;
                const finalHeight = rect.height() - newY;

                if (finalY >= 0 && groupX + newWidth <= stageSize.width &&
                    finalY + finalHeight <= stageSize.height) {
                  rect.width(newWidth);
                  rect.height(finalHeight);
                  group.y(finalY);
                  handle.x(newWidth);
                  handle.y(0);
                }
              }
            }}
            onDragEnd={handleResizeEnd}
          />

          {/* Bottom-left handle */}
          <Circle
            x={0}
            y={cellHeight}
            radius={handleSize / 2}
            fill="#0066cc"
            stroke="white"
            strokeWidth={1}
            draggable
            onDragStart={handleResizeStart}
            onClick={(e) => e.cancelBubble = true}
            onDragMove={(e) => {
              if (!isResizing.current) return;

              const group = groupRef.current;
              if (!group) return;

              const rect = group.findOne('Rect');
              if (!rect) return;

              const handle = e.target;
              const newX = handle.x();
              const newHeight = handle.y();

              if (rect.width() - newX > minSize && newHeight > minSize) {
                // Check boundaries
                const groupX = group.x();
                const groupY = group.y();
                const finalX = groupX + newX;
                const finalWidth = rect.width() - newX;

                if (finalX >= 0 && groupY + newHeight <= stageSize.height &&
                    finalX + finalWidth <= stageSize.width) {
                  rect.width(finalWidth);
                  rect.height(newHeight);
                  group.x(finalX);
                  handle.x(0);
                  handle.y(newHeight);
                }
              }
            }}
            onDragEnd={handleResizeEnd}
          />

          {/* Bottom-right handle */}
          <Circle
            x={cellWidth}
            y={cellHeight}
            radius={handleSize / 2}
            fill="#0066cc"
            stroke="white"
            strokeWidth={1}
            draggable
            onDragStart={handleResizeStart}
            onClick={(e) => e.cancelBubble = true}
            onDragMove={(e) => {
              if (!isResizing.current) return;

              const group = groupRef.current;
              if (!group) return;

              const rect = group.findOne('Rect');
              if (!rect) return;

              const handle = e.target;
              const newWidth = handle.x();
              const newHeight = handle.y();

              if (newWidth > minSize && newHeight > minSize) {
                // Check boundaries
                const groupX = group.x();
                const groupY = group.y();

                if (groupX + newWidth <= stageSize.width &&
                    groupY + newHeight <= stageSize.height) {
                  rect.width(newWidth);
                  rect.height(newHeight);
                  handle.x(newWidth);
                  handle.y(newHeight);
                }
              }
            }}
            onDragEnd={handleResizeEnd}
          />
        </>
      )}
    </Group>
  );
};

export default DraggableCell;